import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from 'react-query';
import instance from 'axiosInstance';

import styles from './GlobalSearch.module.scss';
import SearchResultIcon from './SearchResultIcon';

const ITEMS_PER_PAGE = 20;
const LOAD_MORE_THRESHOLD = 15;

const fetchSearchResults = async ({ queryKey, pageParam = 1 }) => {
  const query = queryKey[1];
  if (!query || query.length < 2)
    return { results: [], pagination: { total: 0, totalPages: 0 } };
  const response = await instance.get(
    `/private/admin/search?q=${encodeURIComponent(
      query
    )}&page=${pageParam}&limit=${ITEMS_PER_PAGE}`
  );
  return response.data;
};

const typeLabels = {
  user: 'User',
  project: 'Project',
  task_url: 'Task',
  task_description: 'Task',
};

const typeColors = {
  user: 'var(--theme-color-primary)',
  project: 'var(--theme-color-accent)',
  task_url: 'var(--theme-color-success)',
  task_description: 'var(--theme-color-warning)',
};

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      setSelectedIndex(-1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // React Query for infinite scroll
  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(['globalSearch', debouncedQuery], fetchSearchResults, {
    enabled: debouncedQuery.length >= 2,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      if (pagination.page < pagination.totalPages) {
        return pagination.page + 1;
      }
      return undefined;
    },
    staleTime: 30000,
  });

  // Flatten all results from all pages
  const allResults = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results);
  }, [data]);

  const totals = data?.pages?.[0]?.totals || {};
  const totalCount = data?.pages?.[0]?.pagination?.total || 0;
  const totalHours = data?.pages?.[0]?.total_hours || 0;

  // Handle scroll for infinite loading
  const handleScroll = useCallback(
    (e) => {
      const list = e.target;
      const items = list.querySelectorAll('li');

      if (
        items.length >= LOAD_MORE_THRESHOLD &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        const thresholdItem =
          items[items.length - (ITEMS_PER_PAGE - LOAD_MORE_THRESHOLD)];
        if (thresholdItem) {
          const itemRect = thresholdItem.getBoundingClientRect();
          const listRect = list.getBoundingClientRect();

          if (itemRect.top < listRect.bottom) {
            fetchNextPage();
          }
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global keyboard shortcut Ctrl+K to open search
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [selectedIndex]);

  const handleResultClick = useCallback(
    (result) => {
      navigate(result.url);
      setIsOpen(false);
      setQuery('');
      setSelectedIndex(-1);
    },
    [navigate]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || allResults.length === 0) {
        if (e.key === 'Escape') {
          setIsOpen(false);
          inputRef.current?.blur();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < allResults.length - 1 ? prev + 1 : prev
          );
          // Load more when approaching end
          if (
            selectedIndex >=
              allResults.length - (ITEMS_PER_PAGE - LOAD_MORE_THRESHOLD) &&
            hasNextPage &&
            !isFetchingNextPage
          ) {
            fetchNextPage();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && allResults[selectedIndex]) {
            handleResultClick(allResults[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSelectedIndex(-1);
          inputRef.current?.blur();
          break;
        default:
          break;
      }
    },
    [
      isOpen,
      allResults,
      selectedIndex,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      handleResultClick,
    ]
  );

  const showDropdown = isOpen && debouncedQuery.length >= 2;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.inputWrapper}>
        <svg
          className={styles.searchIcon}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          placeholder="Search users, projects, tasks..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {(isLoading || isFetching) && debouncedQuery.length >= 2 && (
          <div className={styles.spinner} />
        )}
      </div>

      {showDropdown && (
        <div className={styles.dropdown}>
          {allResults.length === 0 && !isLoading ? (
            <div className={styles.noResults}>
              No results found for &quot;{debouncedQuery}&quot;
            </div>
          ) : (
            <>
              {/* Summary counts */}
              {totalCount > 0 && (
                <div className={styles.summary}>
                  <span className={styles.summaryTotal}>
                    {totalCount} results
                    {totalHours > 0 && ` | ${totalHours.toFixed(2)}h total`}:
                  </span>
                  {totals.users > 0 && (
                    <span
                      className={styles.summaryBadge}
                      style={{ '--badge-color': typeColors.user }}
                    >
                      {totals.users} users
                    </span>
                  )}
                  {totals.projects > 0 && (
                    <span
                      className={styles.summaryBadge}
                      style={{ '--badge-color': typeColors.project }}
                    >
                      {totals.projects} projects
                    </span>
                  )}
                  {totals.task_urls > 0 && (
                    <span
                      className={styles.summaryBadge}
                      style={{ '--badge-color': typeColors.task_url }}
                    >
                      {totals.task_urls} task URLs
                    </span>
                  )}
                  {totals.task_descriptions > 0 && (
                    <span
                      className={styles.summaryBadge}
                      style={{ '--badge-color': typeColors.task_description }}
                    >
                      {totals.task_descriptions} tasks
                    </span>
                  )}
                </div>
              )}

              {/* Results list */}
              <ul
                className={styles.resultsList}
                ref={listRef}
                onScroll={handleScroll}
              >
                {allResults.map((result, index) => (
                  <li
                    key={`${result.type}-${result.id}-${index}`}
                    className={`${styles.resultItem} ${
                      index === selectedIndex ? styles.resultItemSelected : ''
                    }`}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <SearchResultIcon
                      type={result.type}
                      image={result.image}
                      title={result.title}
                    />
                    <div className={styles.resultContent}>
                      <div className={styles.resultTitle}>{result.title}</div>
                      <div className={styles.resultSubtitle}>
                        {result.subtitle}
                      </div>
                    </div>
                    <div className={styles.resultBadges}>
                      {(result.type === 'task_url' ||
                        result.type === 'task_description') && (
                        <span className={styles.hoursBadge}>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {result.hours_spent.toFixed(2)}h
                        </span>
                      )}
                      {result.projectName && (
                        <span
                          className={styles.projectBadge}
                          style={{
                            backgroundColor: typeColors.project,
                          }}
                        >
                          {result.projectName}
                        </span>
                      )}
                      <span
                        className={styles.typeBadge}
                        style={{ backgroundColor: typeColors[result.type] }}
                      >
                        {typeLabels[result.type]}
                      </span>
                    </div>
                  </li>
                ))}
                {isFetchingNextPage && (
                  <li className={styles.loadingMore}>Loading more...</li>
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
